import type { Shift } from '@shared/types/shifts';

export interface ListingWithPrices {
  id: string;
  name: string;
  minimumGuests: number;
  maximumGuests: number;
  temporalListingPrices: TemporalListingPrice[];
  shifts: Shift[];
}

export enum TemporalListingPriceSource {
  Calculated = 'calculated',
  Override = 'override',
}

export interface TemporalListingPrice {
  time: string;
  date: string;
  price: number;
  source: TemporalListingPriceSource;
}

export interface TemporalListingPriceCandidate {
  listingId: string;
  date: string;
  time: string;
  price: number;
}

export interface DailyPriceForecast {
  date: string;
  minPrice: number | null;
  maxPrice: number | null;
}

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export enum MonthOfYear {
  January = 1,
  February = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12,
}

export enum OccupancyRating {
  Slowest = 0,
  Slow = 1,
  Normal = 2,
  Busy = 3,
  Busiest = 4,
}

// MonthOccupancyRating extends the base OccupancyRating scale (0-4) with 2 additional levels
export enum MonthOccupancyRating {
  // Base occupancy ratings (0-4), matching OccupancyRating values
  Slowest = OccupancyRating.Slowest, // 0
  Slow = OccupancyRating.Slow, // 1
  Normal = OccupancyRating.Normal, // 2
  Busy = OccupancyRating.Busy, // 3
  // Extended month-specific ratings (4-6)
  Busier = 4,
  Busiest = 5,
  ExtremelyBusy = 6,
}

export interface TimeOfDayOccupancyRating {
  id: string;
  dayOfWeek: DayOfWeek;
  time: string;
  rating: OccupancyRating;
}

export interface DayOfWeekOccupancyRating {
  id: string;
  dayOfWeek: DayOfWeek;
  rating: OccupancyRating;
}

export interface MonthOfYearOccupancyRating {
  id: string;
  month: MonthOfYear;
  rating: MonthOccupancyRating;
}

export interface TemporalOccupancyRatings {
  timeOfDayOccupancyRatings: TimeOfDayOccupancyRating[];
  dayOfWeekOccupancyRatings: DayOfWeekOccupancyRating[];
  monthOfYearOccupancyRatings: MonthOfYearOccupancyRating[];
}

export type MonthRating = Pick<MonthOfYearOccupancyRating, 'month' | 'rating'>;

// ============ AG Grid types 👇 ==============

export type PriceForTimeSlot = Record<
  string,
  Pick<TemporalListingPrice, 'price' | 'source'>
>;

interface ListingData {
  listingId: string;
  listingName: string;
  shifts: Shift[];
}

export type ListingPricingGridRow = ListingData & PriceForTimeSlot;

export type ShiftTime = Pick<Shift, 'startTime' | 'endTime'>;

export interface PeakPersonalizations {
  dailyPeaks: Omit<TimeOfDayOccupancyRating, 'id'>[];
  weeklyPeaks: number[];
  yearlyPeaks: number[];
}
